import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Users,
  Layout,
  Code,
  Shield,
  Globe,
  PieChart,
} from "lucide-react";

const ProcessSection = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
    });
  }, []);

const whatWeOffer = [
  {
    icon: Globe,
    title: "Customized Training Programs",
    description:
      "Role-based training modules tailored to your staff’s daily responsibilities, ensuring fast adoption and effective learning.",
  },
  {
    icon: Layout,
    title: "Structured Employee Onboarding",
    description:
      "A complete onboarding framework that helps new hires quickly understand processes, tools, and organizational culture.",
  },
  {
    icon: Users,
    title: "Skill Development & Upskilling",
    description:
      "Continuous learning sessions designed to enhance skills, improve productivity, and support your team’s long-term growth.",
  },
  {
    icon: PieChart,
    title: "Performance Tracking & Assessments",
    description:
      "Structured assessments, quizzes, and progress tracking to evaluate staff understanding and training effectiveness.",
  },
  {
    icon: Code,
    title: "Tool & Software Training",
    description:
      "Hands-on training for internal tools, CRMs, ERPs, dashboards, and workflow platforms used across your organization.",
  },
  {
    icon: Shield,
    title: "Compliance, Safety & Policy Training",
    description:
      "Training programs covering safety guidelines, compliance standards, and organization policies to maintain operational discipline.",
  },
];


const processSteps = [
  {
    number: "01",
    title: "Needs Assessment & Training Planning",
    description:
      "We analyze job roles, skill gaps, and workflow requirements to design a structured and effective training roadmap.",
    icon: Users,
      imagePath: "./images/servicesImages/Needs Assessment & Training Planning.png",
  },
  {
    number: "02",
    title: "Content Development & Program Setup",
    description:
      "We prepare training modules, documentation, tasks, and walkthroughs tailored to each department and role.",
    icon: Layout,
      imagePath: "./images/servicesImages/Content Development & Program Setup.jpg",
  },
  {
    number: "03",
    title: "Delivery, Engagement & Hands-On Sessions",
    description:
      "Interactive workshops, demos, real-time practice sessions, and role-based exercises to build confidence and clarity.",
    icon: Code,
      imagePath: "./images/servicesImages/Delivery, Engagement & Hands-On Sessions.png",
  },
  {
    number: "04",
    title: "Evaluation, Certification & Post-Training Support",
    description:
      "We conduct assessments, issue certificates, provide feedback, and offer continuous support to ensure ongoing improvement.",
    icon: Shield,
      imagePath: "./images/servicesImages/Evaluation, Certification & Post-Training Support.png",
  },
];


const ourEdge = [
  "🎯 Personalized Learning Paths: Training programs aligned with staff roles and organizational goals.",
  "📘 Easy-to-Understand Modules: Simple, structured, and interactive content that encourages quick understanding.",
  "💼 Real-Time Practical Sessions: Hands-on exercises that help staff confidently perform daily tasks.",
  "📊 Performance Insights: Detailed tracking of progress, skill growth, and training effectiveness.",
];


const howWeWork = [
  "1. Skill & Role Assessment – We understand team needs, role expectations, and onboarding goals before designing the training plan.",
  "2. Content Design & Setup – We develop structured modules, documents, and walkthroughs based on your organizational workflow.",
  "3. Interactive Training Delivery – Live training, hands-on practice, and tool-based sessions ensure strong understanding.",
  "4. Performance Review & Support – We evaluate progress, provide actionable feedback, and assist with continuous improvement.",
];


  return (
    <section className="py-20 px-[5%] bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        {/* What We Offer Section */}
        <div className="mb-28">
          <h2
            className="text-center text-3xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-12"
            data-aos="fade-up"
          >
            What We Offer
          </h2>
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {whatWeOffer.map((feature, index) => (
              <div
                key={index}
                className="bg-[#dcfaf9] rounded-full p-8 shadow-md border-t-4 border-b-4 border-[#0d6b68] hover:shadow-xl transition-all duration-300"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="flex items-center gap-4 mb-4">
                  <feature.icon className="w-8 h-8 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-32" data-aos="fade-up">
  <h2 className="text-4xl md:text-6xl font-semibold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-6">
    Our Training Process
  </h2>

  <p className="text-gray-600 text-lg sm:text-xl max-w-2xl mx-auto">
    We follow a structured, step-by-step approach to ensure every employee is fully trained, confident, and ready for real-world operations. From understanding role requirements to certification, each stage is designed for maximum clarity and smooth adoption.
  </p>
</div>


        {/* Process Grid */}
        <div className="grid md:grid-cols-2 gap-y-32 gap-x-10">
          {processSteps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <div
                key={index}
                className="flex flex-col relative"
                data-aos="fade-up"
                data-aos-delay={index * 200}
              >
                {/* Background Number */}
                <div className="absolute -top-24 text-6xl md:text-9xl font-bold text-[#9ee8e7] select-none pointer-events-none z-0">
                  {step.number}
                </div>

                {/* Card */}
                <div className="bg-gradient-to-br from-[#0d6b68] to-[#baf7f6] rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 z-10 border border-gray-100">
                  {/* Card Image/Icon Area */}
                  <div className="mb-8 relative">
                    <div className="w-full h-fit bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl flex items-center justify-center overflow-hidden relative">
                      <img
                        src={step.imagePath}
                        alt={step.title}
                        className="w-full h-38 md:h-40 lg:h-52 object-cover rounded-2xl"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextElementSibling.style.display = "flex";
                        }}
                      />

                      {/* Fallback Icon */}
                      <div className="absolute inset-0 hidden items-center justify-center">
                        <div className="relative">
                          <div className="absolute -top-4 -left-4 w-3 h-3 bg-blue-400 rounded-full opacity-60"></div>
                          <div className="absolute -bottom-6 -right-6 w-2 h-2 bg-purple-400 rounded-full opacity-40"></div>
                          <div className="absolute top-2 right-4 w-1.5 h-1.5 bg-indigo-400 rounded-full opacity-50"></div>
                          <div className="bg-white p-6 rounded-2xl shadow-lg">
                            <IconComponent className="w-12 h-12 text-blue-600" />
                          </div>
                          <div className="absolute top-1/2 -left-8 w-6 h-0.5 bg-blue-300 opacity-60"></div>
                          <div className="absolute top-1/2 -right-8 w-6 h-0.5 bg-blue-300 opacity-60"></div>
                          <div className="absolute -top-8 left-1/2 w-0.5 h-6 bg-blue-300 opacity-60"></div>
                          <div className="absolute -bottom-8 left-1/2 w-0.5 h-6 bg-blue-300 opacity-60"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Our Edge Section */}
        <div className="text-center mt-32" data-aos="fade-up">
          <h2
            className="text-4xl md:text-6xl font-semibold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-8"
            style={{ lineHeight: "1.4" }}
          >
            Our Edge
          </h2>

          <ul className="text-gray-700 max-w-4xl mx-auto space-y-4 text-lg">
            {ourEdge.map((point, index) => (
              <li key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                {point}
              </li>
            ))}
          </ul>
        </div>

        {/* How We Work Section */}
        <div className="text-center mt-20" data-aos="fade-up">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-8 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            How We Work
          </h2>
          <ul className="text-gray-700 max-w-3xl mx-auto space-y-4 text-lg">
            {howWeWork.map((step, index) => (
              <li key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                {step}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
